/**
 * Global quick-search command palette (Ctrl/Cmd+K).
 * Mounted once in Layout. Queries GET /search and lets the user jump to any
 * device / location / subnet from anywhere in the app.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, TextField, InputAdornment, List, ListItemButton, ListItemIcon,
  ListItemText, Typography, Box, Chip, CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DevicesIcon from '@mui/icons-material/Devices';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanIcon from '@mui/icons-material/Lan';
import axios from '../api/axios';

const ICONS = {
  device: <DevicesIcon fontSize="small" />,
  location: <LocationOnIcon fontSize="small" />,
  subnet: <LanIcon fontSize="small" />,
};
const KIND_LABEL = { device: 'Устройство', location: 'Локация', subnet: 'Подсеть' };

export default function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const debRef = useRef(null);

  // global hotkey
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // reset on open/close
  useEffect(() => { if (!open) { setQ(''); setResults([]); setActive(0); } }, [open]);

  // debounced search
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!q.trim()) { setResults([]); return; }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get('/search', { params: { q } });
        setResults(res.data.results || []);
        setActive(0);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [q]);

  const go = useCallback((r) => {
    if (!r) return;
    setOpen(false);
    navigate(r.url);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(results[active]); }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm"
            PaperProps={{ sx: { position: 'fixed', top: 80, m: 0 } }}>
      <Box sx={{ p: 1.5 }}>
        <TextField
          autoFocus fullWidth value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
          placeholder="Поиск: устройство, IP, локация, подсеть…"
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            endAdornment: loading ? <CircularProgress size={18} /> : <Chip size="small" label="Ctrl+K" variant="outlined" />,
          }}
        />
      </Box>
      {results.length > 0 && (
        <List dense sx={{ maxHeight: 380, overflow: 'auto', pt: 0 }}>
          {results.map((r, i) => (
            <ListItemButton key={`${r.kind}-${r.id}-${i}`} selected={i === active}
                            onMouseEnter={() => setActive(i)} onClick={() => go(r)}>
              <ListItemIcon sx={{ minWidth: 36 }}>{ICONS[r.kind] || <SearchIcon fontSize="small" />}</ListItemIcon>
              <ListItemText primary={r.title} secondary={r.subtitle} />
              <Chip size="small" label={KIND_LABEL[r.kind] || r.kind} variant="outlined" />
            </ListItemButton>
          ))}
        </List>
      )}
      {q.trim() && !loading && results.length === 0 && (
        <Typography sx={{ p: 2, textAlign: 'center' }} color="text.secondary">Ничего не найдено</Typography>
      )}
    </Dialog>
  );
}
